import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useEffect, useState } from "react";
import { Modal, Button, TextField, PinInput, Loader } from "@/shared/ui";
import { useAuth } from "@/app";
import { requestLoginCode, registerUser, verifyCode, loginWithYandex, loginWithVk, loginWithTelegramOidc, } from "@/shared/api/auth";
import { getApiErrorMessage } from "@/shared/lib/apiError";
import { LEGAL_LINK_URLS } from "@/shared/constants/legalLinks";
import { trackEvent } from "@/shared/lib/analytics";
import { AnalyticsEvent } from "@/shared/lib/analyticsEvents";
import styles from "./AuthModal.module.scss";
/**
 * Единый экран авторизации без табов. Пользователь вводит email →
 * `/auth/login` отвечает: успех → существующий юзер (шаг «код»), 404/400 →
 * новый юзер (шаг «имя» → код). OAuth: Яндекс ID, VK ID, Telegram OIDC.
 */
export const AuthModal = ({ opened, onClose, onSuccess, language, }) => {
    const { refreshUser } = useAuth();
    const ru = language === "ru";
    const telegramOidcEnabled = Boolean(process.env.REACT_APP_TELEGRAM_OIDC_CLIENT_ID);
    const [step, setStep] = useState("start");
    const [email, setEmail] = useState("");
    const [firstName, setFirstName] = useState("");
    const [lastName, setLastName] = useState("");
    const [code, setCode] = useState("");
    const [codeSent, setCodeSent] = useState(false);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [resendTimer, setResendTimer] = useState(0);
    useEffect(() => {
        if (opened) {
            setStep("start");
            setEmail("");
            setFirstName("");
            setLastName("");
            setCode("");
            setCodeSent(false);
            setBusy(false);
            setError(null);
            setResendTimer(0);
        }
    }, [opened]);
    useEffect(() => {
        if (resendTimer > 0) {
            const t = setTimeout(() => setResendTimer(resendTimer - 1), 1000);
            return () => clearTimeout(t);
        }
    }, [resendTimer]);
    const emailValid = /\S+@\S+\.\S+/.test(email);
    const finishAuth = async () => {
        await refreshUser();
        onSuccess();
    };
    const handleStart = async () => {
        if (busy)
            return;
        if (!emailValid) {
            setError(ru ? "Введите корректный email" : "Enter a valid email");
            return;
        }
        setBusy(true);
        setError(null);
        // Старт входа по email (завершение — login — пишет бэк после verify-code).
        trackEvent(AnalyticsEvent.LOGIN_START, { method: "email" });
        try {
            await requestLoginCode({ email });
            setStep("login");
            setCodeSent(true);
            setCode("");
            setResendTimer(45);
        }
        catch (err) {
            const status = err?.response
                ?.status;
            // /auth/login → 404/400 для незарегистрированного email = новый юзер.
            if (status === 404 || status === 400) {
                setStep("register");
                setCodeSent(false);
                setCode("");
                setError(null);
                // Незарегистрированный email → переходим к регистрации.
                trackEvent(AnalyticsEvent.REGISTER_START, { method: "email" });
            }
            else {
                setError(getApiErrorMessage(err) ||
                    (ru ? "Не удалось отправить код" : "Could not send code"));
            }
        }
        finally {
            setBusy(false);
        }
    };
    const handleSendRegisterCode = async () => {
        if (busy)
            return;
        if (!firstName.trim()) {
            setError(ru ? "Введите имя" : "Enter your name");
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await registerUser({
                email,
                first_name: firstName.trim(),
                last_name: lastName.trim() || undefined,
            });
            setCodeSent(true);
            setCode("");
            setResendTimer(45);
        }
        catch (err) {
            setError(getApiErrorMessage(err) ||
                (ru ? "Не удалось отправить код" : "Could not send code"));
        }
        finally {
            setBusy(false);
        }
    };
    const handleVerify = async (value) => {
        if (busy)
            return;
        if (value.length !== 6) {
            setError(ru ? "Введите 6-значный код" : "Enter the 6-digit code");
            return;
        }
        setBusy(true);
        setError(null);
        try {
            await verifyCode({ email, code: value });
            await finishAuth();
        }
        catch (err) {
            // Неверный/просроченный код.
            trackEvent(AnalyticsEvent.CODE_VERIFY_FAILED, { method: "email" });
            setError(getApiErrorMessage(err) || (ru ? "Неверный код" : "Invalid code"));
        }
        finally {
            setBusy(false);
        }
    };
    const handleResend = async () => {
        if (resendTimer > 0 || busy)
            return;
        setBusy(true);
        setError(null);
        // Повторный запрос кода.
        trackEvent(AnalyticsEvent.CODE_RESEND, { method: "email" });
        try {
            if (step === "login") {
                await requestLoginCode({ email });
            }
            else {
                await registerUser({
                    email,
                    first_name: firstName.trim(),
                    last_name: lastName.trim() || undefined,
                });
            }
            setResendTimer(45);
        }
        catch (err) {
            setError(getApiErrorMessage(err) ||
                (ru ? "Не удалось отправить код" : "Could not send code"));
        }
        finally {
            setBusy(false);
        }
    };
    const handleYandex = async () => {
        if (busy)
            return;
        setBusy(true);
        setError(null);
        // Старт входа через Яндекс (завершение — login — пишет бэк в callback).
        trackEvent(AnalyticsEvent.LOGIN_START, { method: "yandex" });
        try {
            const ok = await loginWithYandex();
            if (ok) {
                await finishAuth();
            }
        }
        catch {
            setError(ru ? "Не удалось войти через Яндекс" : "Yandex login failed");
        }
        finally {
            setBusy(false);
        }
    };
    const handleVk = async () => {
        if (busy)
            return;
        setBusy(true);
        setError(null);
        trackEvent(AnalyticsEvent.LOGIN_START, { method: "vk" });
        try {
            const ok = await loginWithVk();
            if (ok) {
                await finishAuth();
            }
        }
        catch {
            setError(ru ? "Не удалось войти через VK" : "VK login failed");
        }
        finally {
            setBusy(false);
        }
    };
    const handleTelegram = async () => {
        if (busy)
            return;
        setBusy(true);
        setError(null);
        trackEvent(AnalyticsEvent.LOGIN_START, { method: "telegram" });
        try {
            const ok = await loginWithTelegramOidc();
            if (ok) {
                await finishAuth();
            }
            else if (!telegramOidcEnabled) {
                setError(ru
                    ? "Telegram-вход не настроен"
                    : "Telegram login is not configured");
            }
        }
        catch {
            setError(ru ? "Не удалось войти через Telegram" : "Telegram login failed");
        }
        finally {
            setBusy(false);
        }
    };
    const handleBack = () => {
        setStep("start");
        setCode("");
        setCodeSent(false);
        setError(null);
        setResendTimer(0);
    };
    const codeChange = (value) => {
        setCode(value);
        if (value.length === 6) {
            setTimeout(() => handleVerify(value), 80);
        }
    };
    const timerStr = `0:${String(resendTimer).padStart(2, "0")}`;
    const showCode = step === "login" || codeSent;
    return (_jsx(Modal, { opened: opened, onClose: onClose, hideCloseButton: true, size: "sm", ariaLabel: ru ? "Авторизация" : "Authentication", children: _jsx("div", { className: styles.root, children: step === "start" ? (_jsxs(_Fragment, { children: [_jsxs("div", { className: styles.topBar, children: [_jsx("span", { style: { width: 32 } }), _jsx("button", { type: "button", className: styles.closeBtn, onClick: onClose, "aria-label": ru ? "Закрыть" : "Close", children: _jsx("svg", { width: "14", height: "14", viewBox: "0 0 14 14", fill: "none", stroke: "currentColor", strokeWidth: "1.8", strokeLinecap: "round", "aria-hidden": "true", children: _jsx("path", { d: "M2 2l10 10M12 2L2 12" }) }) })] }), _jsx("h2", { className: styles.title, children: ru ? "Войти в Виртуоз" : "Sign in to Virtuoso" }), _jsxs("div", { className: styles.oauthStack, children: [_jsxs("button", { type: "button", className: styles.oauthBtn, onClick: handleYandex, disabled: busy, children: [_jsx("span", { className: styles.yandexIcon, "aria-hidden": "true", children: "\u042F" }), ru ? "Продолжить с Яндекс ID" : "Continue with Yandex ID"] }), _jsxs("button", { type: "button", className: styles.oauthBtn, onClick: handleVk, disabled: busy, children: [_jsx("span", { className: styles.vkIcon, "aria-hidden": "true", children: "VK" }), ru ? "Продолжить с VK ID" : "Continue with VK ID"] }), telegramOidcEnabled && (_jsxs("button", { type: "button", className: styles.oauthBtn, onClick: handleTelegram, disabled: busy, children: [_jsx("span", { className: styles.telegramIcon, "aria-hidden": "true", children: _jsx("svg", { width: "14", height: "14", viewBox: "0 0 24 24", fill: "currentColor", "aria-hidden": "true", children: _jsx("path", { d: "M9.04 15.3 8.85 19a1 1 0 0 0 .8-.4l1.92-1.84 3.98 2.92c.73.4 1.25.19 1.44-.68l2.61-12.3h.01c.23-1.07-.39-1.49-1.1-1.23L3.5 9.7c-1.04.4-1.02.98-.18 1.24l3.74 1.17 8.69-5.48c.41-.25.78-.11.47.16L9.04 15.3Z" }) }) }), ru ? "Продолжить с Telegram" : "Continue with Telegram"] }))] }), _jsxs("div", { className: styles.divider, children: [_jsx("span", { className: styles.dividerLine }), _jsx("span", { className: styles.dividerText, children: ru ? "Или по email" : "Or by email" }), _jsx("span", { className: styles.dividerLine })] }), _jsx("form", { onSubmit: (e) => {
                            e.preventDefault();
                            handleStart();
                        }, children: _jsxs("div", { style: { display: "flex", flexDirection: "column", gap: 12 }, children: [_jsx(TextField, { type: "email", placeholder: "\u0432\u044B@example.com", value: email, onChange: (e) => setEmail(e.target.value), error: error || undefined, autoFocus: true }), _jsx(Button, { type: "submit", variant: "primary", size: "md", fullWidth: true, disabled: busy, leftSection: busy ? _jsx(Loader, { size: "sm" }) : undefined, children: ru ? "Продолжить" : "Continue" })] }) }), _jsxs("p", { className: styles.legal, children: [ru
                                ? "Продолжая, вы соглашаетесь с "
                                : "By continuing, you agree to the ", _jsx("a", { href: LEGAL_LINK_URLS.userAgreement, target: "_blank", rel: "noopener noreferrer", children: ru ? "пользовательским соглашением" : "User Agreement" }), ru ? " и " : " and ", _jsx("a", { href: LEGAL_LINK_URLS.privacy, target: "_blank", rel: "noopener noreferrer", children: ru ? "политикой конфиденциальности" : "Privacy Policy" }), "."] })] })) : (_jsxs(_Fragment, { children: [_jsx("div", { className: styles.topBar, children: _jsxs("button", { type: "button", className: styles.backBtn, onClick: handleBack, children: ["\u2190 ", ru ? "Назад" : "Back"] }) }), _jsx("h2", { className: styles.title, children: step === "login"
                            ? ru
                                ? "С возвращением!"
                                : "Welcome back!"
                            : ru
                                ? "Создадим аккаунт"
                                : "Let's create an account" }), _jsxs("p", { className: styles.subtitle, children: [step === "login"
                                ? ru
                                    ? "Отправили код подтверждения на"
                                    : "We sent a confirmation code to"
                                : codeSent
                                    ? ru
                                        ? "Письмо с кодом отправили на"
                                        : "We sent the code to"
                                    : ru
                                        ? "Зарегистрируем аккаунт на"
                                        : "We'll register an account for", _jsx("span", { className: styles.email, children: email })] }), step === "register" && (_jsxs("div", { className: styles.nameGrid, children: [_jsx(TextField, { label: ru ? "Имя" : "First name", value: firstName, onChange: (e) => setFirstName(e.target.value), disabled: codeSent, autoFocus: true }), _jsx(TextField, { label: ru ? "Фамилия · опц." : "Last name · opt.", value: lastName, onChange: (e) => setLastName(e.target.value), disabled: codeSent })] })), showCode && (_jsxs("div", { className: styles.codeBlock, children: [_jsx("span", { className: styles.spamHint, children: ru ? "Проверьте СПАМ!" : "Check your spam folder!" }), _jsx("span", { className: styles.fieldLabel, children: ru ? "Код из письма" : "Code from email" }), _jsx(PinInput, { length: 6, value: code, onChange: codeChange, disabled: busy, invalid: !!error })] })), error && _jsx("span", { className: styles.error, children: error }), step === "register" && !codeSent ? (_jsx(Button, { variant: "primary", size: "md", fullWidth: true, onClick: handleSendRegisterCode, disabled: busy || !firstName.trim(), leftSection: busy ? _jsx(Loader, { size: "sm" }) : undefined, children: ru ? "Продолжить" : "Continue" })) : (_jsx(Button, { variant: "primary", size: "md", fullWidth: true, onClick: () => handleVerify(code), disabled: busy || code.length !== 6, leftSection: busy ? _jsx(Loader, { size: "sm" }) : undefined, children: step === "login"
                            ? ru
                                ? "Войти"
                                : "Sign in"
                            : ru
                                ? "Создать аккаунт"
                                : "Create account" })), showCode && (_jsxs("div", { className: styles.resendRow, children: [_jsx("button", { type: "button", className: styles.resendBtn, onClick: handleResend, disabled: resendTimer > 0 || busy, children: ru ? "Отправить ещё раз" : "Resend code" }), resendTimer > 0 && (_jsx("span", { className: styles.timer, children: timerStr }))] }))] })) }) }));
};
